import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, Navigate, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowLeft, UploadSimple} from '../../icons';
import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {ClientTabs} from '../ClientTabs';
import {SOURCE_LABEL_KEYS, formatEconomyDate} from '../panel_v11/economyFormatters';
import {EconomiaForm} from './EconomiaForm';
import {EconomiaUnlocks} from './EconomiaUnlocks';
import {EconomiaLines} from './EconomiaLines';
import {EconomiaVersions} from './EconomiaVersions';
import {EscalafySection} from './EscalafyConnectWizard';
import {EscalafySourceStrip} from './EscalafySourceStrip';
import {ImportEconomicsDialog} from './ImportEconomicsDialog';
import {AGENCY_CONNECTIONS_PATH, credentialFromSearch, pathWithoutCredential} from './state/escalafyModel';
import {escalafyFieldSources, escalafyStripState} from './state/escalafyProvenance';
import {
    currencyOptions,
    formIsDirty,
    invalidFieldsOf,
    replacesSameDate,
    spendCapProgress,
    toPayload,
    unlocksFor,
    validateEconomics,
    valuesFromProfile,
} from './state/economiaModel';
import '../panel_v11/PanelV11.scss';
import './Economia.scss';

/**
 * Pantalla «Economía del negocio» de un cliente (`/client/:id/economia`, UX 2.1; mockups 2 y 3).
 *
 * Es la carga, que en los mockups era un panel lateral: la ficha ya no abre paneles (card
 * bBcBPqGg). Arriba el formulario de los cinco datos con «Con lo que cargaste, ya tienes» al lado
 * —se recalcula al tipear—; abajo el override por línea de seguimiento, las versiones, la conexión
 * con Escalafy y la importación por planilla.
 *
 * Con Escalafy vinculado, el formulario dice ARRIBA que hay datos que llegan solos (la franja) y marca
 * cada campo que trajo (primera conexión real, Gótica, 01/10: «el usuario no se entera»). Mientras la
 * sección de abajo espera una sincronización, la franja dice «Actualizando…».
 *
 * A diferencia de la card, esta pantalla SÍ pide su dato (`GET client/<id>/economics/`). Cada guardado
 * es una versión NUEVA: el formulario arranca con los valores vigentes y la vigencia en hoy.
 */
export function EconomiaPage({clientId}) {
    const {t, i18n} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const hash = String(location.hash || '').replace('#', '');
    // La vuelta del OAuth de Escalafy: se lee UNA vez y se saca de la URL (R-ecofe-05). La sección la
    // usa para abrir el paso 2 y avisa, y acá se olvida.
    const [credentialId, setCredentialId] = useState(() => credentialFromSearch(location.search));

    const [data, setData] = useState(null);
    // `null` = sin error (patrón `LoadErrorState`): un string vacío también es un error.
    const [loadError, setLoadError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [values, setValues] = useState(null);
    const [errors, setErrors] = useState({});
    const [showError, setShowError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [versions, setVersions] = useState(null);
    const [versionsToken, setVersionsToken] = useState(0);
    const [clients, setClients] = useState([]);
    const [importOpen, setImportOpen] = useState(false);
    // La sección de Escalafy está esperando una sincronización (ver `EscalafySection`).
    const [escalafySyncing, setEscalafySyncing] = useState(false);
    // Un «Guardar» en vuelo: dos submits en el mismo tick salían como dos versiones (el `saving` del
    // estado llega tarde) y el segundo corregía al primero en la Bitácora (R-ecofe-K05).
    const saveInFlight = useRef(false);
    // Lo que se está tipeando, para que un recargo por otra vía no lo pise (ver el efecto de carga).
    const liveForm = useRef({values: null, data: null});
    liveForm.current = {values, data};
    const scrolledTo = useRef(null);

    const applyData = useCallback((next) => {
        setData(next);
        setValues(valuesFromProfile(next));
        setErrors({});
        setShowError(false);
    }, []);

    useEffect(() => {
        if (!credentialFromSearch(location.search)) return;
        navigate(pathWithoutCredential(location), {replace: true});
    }, [location, navigate]);

    useEffect(() => {
        let alive = true;
        setLoadError(null);
        ClientEconomicsApi.getProfile(clientId).then((response) => {
            if (!alive) return;
            if (!response || response.status !== 'OK' || !response.data) {
                // 404 uniforme o red: no se distingue (aislamiento) y no se muestra el texto crudo.
                setLoadError('');
                return;
            }
            // Recargar después de la planilla, de Escalafy o de una línea NO pisa un formulario a medio
            // tipear (R-ecofe-28): se actualiza el dato y los valores quedan como están.
            const {values: typed, data: shown} = liveForm.current;
            if (typed && shown && formIsDirty(typed, shown)) {
                setData(response.data);
                return;
            }
            applyData(response.data);
        }).catch(() => { if (alive) setLoadError(''); });
        return () => { alive = false; };
    }, [clientId, reloadToken, applyData]);

    // El nombre del cliente para el título y la lista para el mapeo de Escalafy: el MISMO request
    // que ya hacen el breadcrumb y el Booster (`cache: true` comparte el que esté en vuelo).
    useEffect(() => {
        let alive = true;
        new APIClient(`${API_URL}agency/clients/active`).call('GET', {}, {cache: true}).then((response) => {
            if (alive && response && response.status === 'OK' && Array.isArray(response.data)) setClients(response.data);
        }).catch(() => {});
        return () => { alive = false; };
    }, []);

    // `#escalafy` / `#versiones` (los links de la card): se baja a la sección UNA vez, cuando ya existe.
    // Con `data` en las deps, cada guardado volvía a bajar a `#versiones` (R-ecofe-28).
    useEffect(() => {
        if (!data || !hash || scrolledTo.current === hash) return;
        const target = document.getElementById(hash);
        if (!target) return;
        scrolledTo.current = hash;
        if (target.scrollIntoView) target.scrollIntoView({block: 'start'});
    }, [data, hash]);

    const retry = () => {
        setLoadError(null);
        setData(null);
        setReloadToken((token) => token + 1);
    };

    const handleChange = useCallback((field, value) => {
        setValues((current) => ({
            ...current,
            [field]: value,
            ...(field === 'avg_ticket' ? {avgTicketTouched: true} : {}),
        }));
    }, []);

    const handleCancel = () => applyData(data);

    const failSave = (response) => {
        saveInFlight.current = false;
        setSaving(false);
        // Sólo un error de VALIDACIÓN marca campos; el resto (módulo apagado, 404, red) no tiene campo
        // que corregir y va al genérico (R-ecofe-15).
        const fields = invalidFieldsOf(response);
        if (!fields) {
            toast.error(t('economics.error.generic'));
            return;
        }
        setErrors(fields);
        setShowError(true);
    };

    const handleSave = () => {
        if (saveInFlight.current) return;
        const check = validateEconomics(values);
        if (!check.valid) {
            setErrors(check.fields);
            setShowError(true);
            return;
        }
        saveInFlight.current = true;
        setSaving(true);
        // Un intento válido borra las marcas del intento anterior: si no, la fecha que ya se
        // corrigió seguía en rojo mientras el POST salía (QA-eco-N01).
        setErrors({});
        setShowError(false);
        ClientEconomicsApi.saveProfile(clientId, toPayload(values)).then((response) => {
            if (!response || response.status !== 'OK' || !response.data) {
                failSave(response);
                return;
            }
            saveInFlight.current = false;
            setSaving(false);
            setVersionsToken((token) => token + 1);
            // El toast nombra la versión GUARDADA (`saved`), no la vigente (`profile`): con vigencia
            // mañana, o atrasada habiendo una más nueva, no son la misma (R-ecofe-12).
            const saved = response.data.saved || {};
            const current = response.data.profile;
            if (!saved.id || (current && current.id === saved.id)) {
                toast.success(t('economics.form.saved', {version: saved.version || (current && current.version) || '—'}));
                applyData(response.data);
                return;
            }
            // La guardada no rige hoy: el formulario se queda con lo que se tipeó (volver a la vigente
            // parecería que no guardó) y se dice.
            toast.info(t('economics.form.saved_not_current', {version: saved.version || '—'}));
            setData(response.data);
        }).catch(() => failSave(null));
    };

    // Un cambio en una línea devuelve el `data` completo: se usa tal cual (sin otro GET).
    const handleLinesChanged = useCallback((next) => {
        if (next) {
            setData(next);
        } else {
            setReloadToken((token) => token + 1);
        }
        setVersionsToken((token) => token + 1);
    }, []);

    // Escalafy sincronizó o se usaron sus datos: el perfil y las versiones cambiaron.
    const handleEscalafyChanged = useCallback(() => {
        setReloadToken((token) => token + 1);
        setVersionsToken((token) => token + 1);
    }, []);

    const scrollToEscalafy = () => {
        const target = document.getElementById('escalafy');
        if (target && target.scrollIntoView) target.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    const clientName = useMemo(() => {
        const found = clients.find((client) => String(client.id) === String(clientId));
        return found ? found.name : '';
    }, [clients, clientId]);
    const onCredentialUsed = useCallback(() => setCredentialId(null), []);

    const capProgress = spendCapProgress(data);
    const unlockRows = values ? unlocksFor(values, data, {
        capDaysMissing: capProgress.ready ? null : capProgress.missing,
    }) : [];

    const renderMeta = () => {
        const profile = data && data.profile;
        if (!data || data.configured !== true || !profile) return null;
        const key = SOURCE_LABEL_KEYS[profile.source];
        return (
            <p className="eco-page-meta">
                {t('client_panel.economy.subtitle_source', {
                    source: key ? t(key) : (profile.source_label || profile.source || '—'),
                    version: profile.version || '—',
                    date: formatEconomyDate(profile.effective_from, i18n && i18n.language) || '—',
                    interpolation: {escapeValue: false},
                })}
            </p>
        );
    };

    const renderBody = () => {
        if (loadError !== null) {
            return (
                <LoadErrorState title={t('economics.page.load_error')}
                                text={t('economics.page.load_error_explanation')}
                                retryLabel={t('client_panel.error_retry')}
                                onRetry={retry}/>
            );
        }
        if (!data || !values) return <div className="box-loader"><Spinner color="positive"/></div>;
        // El módulo se apagó para la agencia (o la ruta se abrió con un cache viejo): la ficha.
        if (data.enabled === false) return <Navigate to={`/client/${clientId}`} replace/>;

        const escalafy = data.escalafy || null;
        const stripState = escalafyStripState(escalafy, data.profile, {syncing: escalafySyncing});
        // Guardar a mano con el perfil de Escalafy vigente lo congela: desde ahí la versión manual gana y
        // Escalafy sólo propone (`EscalafySyncService._manual_version_wins`). Se dice ANTES de guardar.
        const escalafyOwnsProfile = !!(escalafy && escalafy.connected === true && data.profile
            && data.profile.source === 'ESCALAFY');

        return (
            <>
                <div className="eco-page-grid">
                    <div className="eco-page-main">
                        <EconomiaForm
                            idPrefix="eco"
                            values={values}
                            errors={errors}
                            currencies={currencyOptions(data, values.currency)}
                            onChange={handleChange}
                            onSubmit={handleSave}
                            onCancel={handleCancel}
                            saving={saving}
                            showError={showError}
                            sameDateNotice={replacesSameDate(values.effective_from, data, versions)}
                            banner={stripState && (
                                <EscalafySourceStrip state={stripState} escalafy={escalafy}
                                                     onSeeConnection={scrollToEscalafy}
                                                     onUsed={handleEscalafyChanged}/>
                            )}
                            fieldSources={escalafyFieldSources(escalafy, data.profile, values)}
                            saveNotice={escalafyOwnsProfile && formIsDirty(values, data)
                                ? t('economics.form.escalafy_save_notice') : null}
                        />
                    </div>
                    <aside className="eco-page-side">
                        <EconomiaUnlocks rows={unlockRows}/>
                    </aside>
                </div>

                <EconomiaLines data={data} clientId={clientId} onChanged={handleLinesChanged}/>

                <EconomiaVersions clientId={clientId} currency={data.currency}
                                  initiallyOpen={hash === 'versiones'} refreshToken={versionsToken}
                                  onLoaded={setVersions}/>

                <EscalafySection clientId={clientId} clients={clients} credentialId={credentialId}
                                 onCredentialUsed={onCredentialUsed}
                                 onManageConnections={() => navigate(AGENCY_CONNECTIONS_PATH)}
                                 onChanged={handleEscalafyChanged}
                                 onSyncingChange={setEscalafySyncing}/>

                <section className="eco-fs eco-import-section" aria-labelledby="eco-import-section-title">
                    <div className="eco-fs__head">
                        <h2 className="eco-fs__title" id="eco-import-section-title">{t('economics.import.title')}</h2>
                    </div>
                    <p className="eco-fs__help">{t('economics.import.body')}</p>
                    <div className="eco-fs__acts">
                        <button type="button" className="pnl-btn pnl-btn--ghost" onClick={() => setImportOpen(true)}
                                data-testid="eco-import-open">
                            <UploadSimple size={14} weight="bold"/>
                            {t('clients_v2.economics.import_cta')}
                        </button>
                    </div>
                </section>
            </>
        );
    };

    return (
        <section className="section-content">
            <ClientTabs clientId={clientId}/>

            <div className="pnl-screen eco-screen" data-testid="eco-screen">
                <Link className="eco-back" to={`/client/${clientId}`}>
                    <ArrowLeft size={14} weight="bold"/>
                    {t('economics.page.back')}
                </Link>
                <header className="eco-page-head">
                    {/* Un cliente inactivo no está en `clients/active`: sin su nombre, el título a secas y
                        no «Economía del negocio · » cortado (R-ecofe-28). */}
                    <h1 className="eco-page-title">
                        {clientName
                            ? t('economics.page.title', {client: clientName, interpolation: {escapeValue: false}})
                            : t('client_panel.economy.title')}
                    </h1>
                    {renderMeta()}
                </header>
                {renderBody()}
            </div>

            <ImportEconomicsDialog isOpen={importOpen} onClose={() => setImportOpen(false)}
                                   onApplied={() => setReloadToken((token) => token + 1)}/>
        </section>
    );
}

export default EconomiaPage;
